/**
 * "What's new" tour of the AI Studio (Robert, 18.10.2026). A pill left of the Build/Test switch opens it: the card
 * grows out of the pill, walks through the new parts with a soft spotlight on each, and shrinks back into the pill
 * at the end. It can open by itself once per browser session until the owner picks "Do not show again" – saved like
 * the platform's other modals (POST /api/user_modal_preferences, modal_name ai_studio_tour); that is switched off for
 * now (TOUR_AUTO_OPEN). A click on the pill always opens it. Steps whose part is not on the screen (narrow layout,
 * credits off, no overview yet) are left out.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faCircleCheck, faCoins, faComments, faFlask, faPuzzlePiece, faWandMagicSparkles, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { MODE_EVENT } from './homeView';
import { popupBusy, releasePopup, takePopup } from '../../utils/popupLock';

const API = process.env.REACT_APP_API_BASE_URL;
export const TOUR_MODAL_NAME = 'ai_studio_tour';
const SESSION_KEY = 'agentStudio.tourShown';
// Off for everyone for now (Robert, 29.10.2026: too many popups). The pill stays, with its dot until "Do not show again".
const TOUR_AUTO_OPEN = false;
// what each step points at (null: the pill itself); visible parts of the open Build view only
export const TOUR_STEPS = [
  { id: 'intro', icon: faWandMagicSparkles, target: null },
  { id: 'composer', icon: faComments, target: '.as-view:not(.is-hidden) .as-dock .studio-composer' },
  { id: 'panel', icon: faPuzzlePiece, target: '.as-view:not(.is-hidden) .as-build-agent' },
  { id: 'test', icon: faFlask, target: '.as-modeswitch button:last-child' },
  { id: 'overview', icon: faChartLine, target: '.as-view:not(.is-hidden) .as-wb-card:not(.is-skeleton)' },
  { id: 'credits', icon: faCoins, target: '.bot-usage-footer[data-credits="on"]' },
  { id: 'done', icon: faCircleCheck, target: null },
];
const CARD_W = 360;
const GAP = 14; // between the highlighted part and the card
const EDGE = 16; // the card keeps this distance to the window's edges
const PAD = 6; // the spotlight around the part
const MS = 380; // opening and closing

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function rectOf(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? { top: r.top, left: r.left, width: r.width, height: r.height } : null;
}

/** the card next to the part: below, above, right or left – the first side where it fits; otherwise centred */
export function placeCard(target, card, view) {
  const clampX = (x) => Math.max(EDGE, Math.min(x, view.width - card.width - EDGE));
  const clampY = (y) => Math.max(EDGE, Math.min(y, view.height - card.height - EDGE));
  const midX = target.left + target.width / 2 - card.width / 2;
  const midY = target.top + target.height / 2 - card.height / 2;
  const below = target.top + target.height + GAP;
  if (below + card.height <= view.height - EDGE) return { x: clampX(midX), y: below };
  const above = target.top - GAP - card.height;
  if (above >= EDGE) return { x: clampX(midX), y: above };
  const right = target.left + target.width + GAP;
  if (right + card.width <= view.width - EDGE) return { x: right, y: clampY(midY) };
  const left = target.left - GAP - card.width;
  if (left >= EDGE) return { x: left, y: clampY(midY) };
  return { x: clampX((view.width - card.width) / 2), y: clampY((view.height - card.height) / 2) };
}

function userId() {
  try { return localStorage.getItem('user_id'); } catch (_) { return null; }
}

export default function StudioTour({ autoOpen = TOUR_AUTO_OPEN }) {
  const { t } = useTranslation();
  const launcher = useRef(null);
  const cardRef = useRef(null);
  const primary = useRef(null);
  const [phase, setPhase] = useState('closed'); // closed | opening | open | closing
  const [steps, setSteps] = useState(TOUR_STEPS);
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState(null); // the highlighted rect
  const [cardSize, setCardSize] = useState({ width: CARD_W, height: 240 });
  const [dismissed, setDismissed] = useState(true); // until the check says otherwise: no "new" dot
  const step = steps[index] || steps[0];

  const targetOf = useCallback((s) => (s.target ? document.querySelector(s.target) : launcher.current), []);

  const open = useCallback(() => {
    takePopup('studio_tour'); // another self-opening popup (the LinkedIn bonus) waits until the tour is closed
    window.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: 'build' })); // the tour shows the Build view
    // the parts on the screen right now; the first and last step always (they point at the pill)
    setTimeout(() => {
      setSteps(TOUR_STEPS.filter((s) => !s.target || rectOf(document.querySelector(s.target))));
      setIndex(0);
      setSpot(rectOf(launcher.current));
      setPhase('opening');
    }, 0);
  }, []);

  const close = useCallback(() => {
    setPhase('closing');
    setSpot(rectOf(launcher.current));
    setTimeout(() => { setPhase('closed'); releasePopup('studio_tour'); }, reducedMotion() ? 0 : MS);
    launcher.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => () => releasePopup('studio_tour'), []); // leaving the Studio with the tour open frees the lock

  const neverAgain = useCallback(async () => {
    setDismissed(true);
    close();
    const uid = userId();
    if (!uid) return;
    try {
      await fetch(`${API}user_modal_preferences`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: parseInt(uid, 10), modal_name: TOUR_MODAL_NAME }),
      });
    } catch (_) { /* the next visit asks again */ }
  }, [close]);

  // once per browser session by itself, until "Do not show again"; it waits until the start page stands
  useEffect(() => {
    const uid = userId();
    if (!uid) return undefined;
    let alive = true;
    let timer = null;
    fetch(`${API}user_modal_preferences/${uid}/check/${TOUR_MODAL_NAME}`)
      .then((r) => (r.ok ? r.json() : { is_dismissed: true }))
      .then((d) => {
        if (!alive) return;
        setDismissed(!!d.is_dismissed);
        let shown = true;
        try { shown = sessionStorage.getItem(SESSION_KEY) === '1'; } catch (_) { /* storage unavailable */ }
        if (!autoOpen || d.is_dismissed || shown) return;
        try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) { /* storage unavailable */ }
        let waited = 0;
        const ready = () => {
          if (!alive) return;
          if (popupBusy('studio_tour')) { timer = setTimeout(ready, 1000); return; } // e.g. the LinkedIn bonus is open
          waited += 300;
          const composer = document.querySelector(TOUR_STEPS[1].target);
          const overview = document.querySelector(TOUR_STEPS[4].target);
          if ((composer && overview) || waited >= 6000) open();
          else timer = setTimeout(ready, 300);
        };
        timer = setTimeout(ready, 900);
      })
      .catch(() => {});
    return () => { alive = false; clearTimeout(timer); };
  }, [autoOpen, open]);

  // where the spotlight sits: the step's part, kept in place on resize and scroll
  useEffect(() => {
    if (phase !== 'opening' && phase !== 'open') return undefined;
    const el = targetOf(step);
    if (el && step.target) el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    const measure = () => setSpot(rectOf(targetOf(step)) || rectOf(launcher.current));
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [phase, step, targetOf]);

  // opening: one frame at the pill, then out to the first step
  useEffect(() => {
    if (phase !== 'opening') return undefined;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setPhase('open')));
    return () => cancelAnimationFrame(id);
  }, [phase]);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const size = { width: el.offsetWidth || CARD_W, height: el.offsetHeight || 240 };
    setCardSize((cur) => (cur.width === size.width && cur.height === size.height ? cur : size));
  }, [index, phase, steps]);

  useEffect(() => {
    if (phase === 'open') primary.current?.focus({ preventScroll: true });
  }, [phase, index]);

  const last = index === steps.length - 1;
  const next = useCallback(() => (last ? close() : setIndex((i) => Math.min(i + 1, steps.length - 1))), [last, close,
    steps.length]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  useEffect(() => {
    if (phase !== 'open') return undefined;
    const key = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [phase, close, next, back]);

  const view = { width: window.innerWidth, height: window.innerHeight };
  const home = rectOf(launcher.current) || { top: EDGE, left: EDGE, width: 0, height: 0 };
  const at = placeCard(spot || home, cardSize, view);
  const out = phase === 'opening' || phase === 'closing'; // the card sits in the pill
  const homeX = home.left + home.width / 2 - cardSize.width / 2;
  const homeY = home.top + home.height / 2 - cardSize.height / 2;
  // at rest the card sits on whole pixels without a transform, so text and icons stay sharp; the scale only
  // runs while it grows out of the pill and goes back in (a layer scaled up from 0.18 stays blurry in Chrome)
  const cardStyle = out
    ? { top: Math.round(homeY), left: Math.round(homeX), transform: 'scale(0.18)', opacity: 0 }
    : { top: Math.round(at.y), left: Math.round(at.x), transform: 'none', opacity: 1 };
  const spotStyle = spot ? {
    top: spot.top - PAD, left: spot.left - PAD, width: spot.width + 2 * PAD, height: spot.height + 2 * PAD,
  } : null;
  const labels = {
    add: t('agent_studio_skill_add'), test: t('agent_studio_tab_test'), improve: t('agent_studio_welcome_improve'),
  };
  const titleId = 'as-tour-title';

  return (
    <>
      <button type="button" ref={launcher} className={`as-tour-launch ${phase !== 'closed' ? 'is-active' : ''}`}
        onClick={() => (phase === 'closed' ? open() : close())} aria-expanded={phase === 'open'}
        title={t('as_tour_launcher_hint')}>
        <FontAwesomeIcon icon={faWandMagicSparkles} />
        <span>{t('as_tour_launcher')}</span>
        {!dismissed && (
          // dot and radar ring share one centre (an SVG, so nothing can shift them against each other)
          <svg className="as-tour-dot" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <circle className="as-tour-ping" cx="8" cy="8" r="3" />
            <circle cx="8" cy="8" r="3" />
          </svg>
        )}
      </button>
      {phase !== 'closed' && createPortal(
        <div className={`as-modal as-tour ${reducedMotion() ? 'is-still' : ''} ${phase === 'open' ? 'is-open' : ''}`}>
          <div className="as-tour-block" aria-hidden="true" />
          {spotStyle && <div className={`as-tour-spot ${step.target ? '' : 'is-home'}`} style={spotStyle} aria-hidden="true" />}
          <div ref={cardRef} className="as-tour-card" style={cardStyle} role="dialog" aria-modal="true"
            aria-labelledby={titleId}>
            <div className="as-tour-head">
              <span className="as-tour-icon"><FontAwesomeIcon icon={step.icon} /></span>
              <span className="as-tour-count">{t('as_tour_step', { n: index + 1, total: steps.length })}</span>
              <button type="button" className="as-tour-close" onClick={close} aria-label={t('close')}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <h3 id={titleId} className="as-tour-title">{t(`as_tour_${step.id}_title`)}</h3>
            <p className="as-tour-text">{t(`as_tour_${step.id}_text`, labels)}</p>
            <div className="as-tour-dots" aria-hidden="true">
              {steps.map((s, i) => <span key={s.id} className={i === index ? 'is-on' : ''} />)}
            </div>
            <div className="as-tour-foot">
              <button type="button" className="as-tour-never" onClick={neverAgain}>{t('do_not_show_again')}</button>
              <span className="as-tour-nav">
                {index === 0 ? (
                  <button type="button" className="as-tour-btn is-quiet" onClick={close}>{t('as_tour_later')}</button>
                ) : (
                  <button type="button" className="as-tour-btn is-quiet" onClick={back}>{t('as_tour_back')}</button>
                )}
                <button type="button" ref={primary} className="as-tour-btn" onClick={next}>
                  {index === 0 ? t('as_tour_start') : last ? t('as_tour_done') : t('as_tour_next')}
                </button>
              </span>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
